import { Button, Tooltip } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { IconPaperclip } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UploadFileModal } from 'src/Components/Modals/UploadFileModal';

import styles from './UploadFileButton.module.scss';
import { Props } from './UploadFileButton.types';

const UploadFileButton = (props: Props) => {
  const {
    loading,
    single,
    label,
    iconOnly,
    onClick,
    onFileSelected,
    onFilesSelected,
    onExistingFileSelect,
    onExistingFilesSelect,
    modalTitle,
    cameraLabel,
    cameraSubtitle,
    fileUploadLabel,
    fileUploadSubtitle,
    existingFileLabel,
    existingFileSubtitle,
    onSourceSelected,
    ...rest
  } = props;
  const [opened, setOpened] = useState(false);
  const { t } = useTranslation();

  const handleDrop = (files: FileWithPath[]) => {
    if (single && onFileSelected) {
      if (files.length > 0) {
        onFileSelected(files[0]);
      }
    } else if (!single && onFilesSelected) {
      onFilesSelected(files);
    }
    setOpened(false);
  };

  const handleExistingFileSelect = (fileId: string) => {
    if (single && onExistingFileSelect) {
      onExistingFileSelect(fileId);
    }
    setOpened(false);
  };

  const handleExistingFilesSelect = (fileIds: string[]) => {
    if (!single && onExistingFilesSelect) {
      onExistingFilesSelect(fileIds);
    }
    setOpened(false);
  };

  const sharedModalProps = {
    opened,
    onClose: () => setOpened(false),
    onDrop: handleDrop,
    modalTitle,
    cameraLabel,
    cameraSubtitle,
    fileUploadLabel,
    fileUploadSubtitle,
    existingFileLabel,
    existingFileSubtitle,
    onSourceSelected,
  };

  const accessibleLabel =
    label ?? t('uploadFileButton.upload', 'Ajouter des documents, photos ou vidéos');

  const button = (
    <Button
      className={iconOnly ? styles.iconOnlyButton : styles.button}
      onClick={onClick ?? (() => setOpened(true))}
      size={'xs'}
      leftSection={iconOnly ? undefined : <IconPaperclip size={16} />}
      loading={loading}
      aria-label={iconOnly ? accessibleLabel : undefined}
      {...rest}
    >
      {iconOnly ? <IconPaperclip size={18} /> : accessibleLabel}
    </Button>
  );

  return (
    <>
      <div className={styles.container}>
        {iconOnly ? (
          <Tooltip label={accessibleLabel} withArrow>
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </div>
      {single ? (
        <UploadFileModal
          {...sharedModalProps}
          onExistingFileSelect={handleExistingFileSelect}
          single
        />
      ) : (
        <UploadFileModal {...sharedModalProps} onExistingFilesSelect={handleExistingFilesSelect} />
      )}
    </>
  );
};

export default UploadFileButton;
